import React from 'react'
import { EnvironmentOutlined } from '@ant-design/icons'
import { Button, Select, Spin, Typography, message } from 'antd'
import debounce from 'lodash.debounce'
import { appApi } from 'api/app'
import { cdekApi } from 'api/cdek'
import { ozonDeliveryApi } from 'api/ozonDelivery'
import { sellerApi } from 'api/seller'
import { yandexDeliveryApi } from 'api/yandexDelivery'
import { IAddressSuggestion } from 'models/app'
import { ICDEKOffice } from 'models/cdek'
import { TVariantSeller } from 'models/seller'
import { DEBOUNCE_DELAY } from 'shared/constants/app'
import { showMessage } from 'shared/utils/showMessage'
import { showServerError } from 'shared/utils/showServerError'
import { loadOzonDropoffCatalog } from 'shared/utils/ozonDropoffCatalog'
import {
  BOUNDS_DELAY,
  CDEK_LOADED_RADIUS_KM,
  CITY_ZOOM,
  CURRENT_PRESET,
  ICityOption,
  IMapPoint,
  MAP_LOOK,
  PICKED_PRESET,
  TCoords,
  distanceKm,
  fromCdekOffice,
  fromOzonDropoffPoint,
  fromYandexStation,
  getCdekCell,
  ozonDropoffAddress
} from 'shared/utils/shipmentPoints'
import { loadYandexMaps } from 'shared/utils/yandexMaps'
import styles from './SellerShipmentPoint.module.scss'

interface ISellerShipmentPointProps {
  provider: 'CDEK' | 'YANDEX' | 'OZON'
  type: TVariantSeller
  sellerId: string
}

// point пустой, если Яндекс не нашёл сохранённую станцию
interface ICurrentPoint {
  code: string
  point: IMapPoint | null
  label?: string
}

const TITLE: Record<ISellerShipmentPointProps['provider'], string> = {
  CDEK: 'Пункт сдачи СДЭК',
  YANDEX: 'Пункт сдачи Яндекс Доставки и 5Post',
  OZON: 'Пункт сдачи Ozon Delivery'
}

const CARRIER: Record<ISellerShipmentPointProps['provider'], string> = {
  CDEK: 'СДЭК',
  YANDEX: 'Яндекс Доставки',
  OZON: 'Ozon Delivery'
}

const loadCurrentPoint = async (
  provider: ISellerShipmentPointProps['provider'],
  sellerId: string
): Promise<ICurrentPoint | null> => {
  if (provider === 'CDEK') {
    const { data } = await sellerApi.getSelectedDeliveryPoint(sellerId)
    return data?.code ? { code: data.code, point: fromCdekOffice(data) } : null
  }

  // У продавца хранится только идентификатор, адрес берём у перевозчика.
  const { data: seller } = await sellerApi.getSeller(sellerId)
  if (provider === 'OZON') {
    const id = seller.ozon_dropoff_point_id ?? seller.logistics?.ozon_dropoff_point_id
    if (!id) return null
    const { data } = await ozonDeliveryApi.getDropoffPointInfo(Number(id))
    const source = data.dropoff_points.find((point) => Number(point.dropoff_point_id) === Number(id))
    return {
      code: String(id),
      point: source ? fromOzonDropoffPoint(source) : null,
      label: source ? ozonDropoffAddress(source) : `Пункт № ${id}`
    }
  }
  const code =
    seller.yandex_dropoff_station_id ??
    seller.logistics?.yandex_dropoff_station_id
  if (!code) return null
  const { data } = await yandexDeliveryApi.getDropoffStations({
    pickup_point_ids: [code]
  })
  const station = data.find((item) => item.code === code || item.uuid === code)
  return { code, point: station ? fromYandexStation(station) : null }
}

export const SellerShipmentPoint: React.FC<ISellerShipmentPointProps> = ({
  provider,
  type,
  sellerId
}) => {
  const mapContainerId = `shipment-point-map-${provider}-${sellerId}`

  const mapRef = React.useRef<any>(null)
  const objectManagerRef = React.useRef<any>(null)
  const pointsRef = React.useRef(new Map<string, IMapPoint>())
  const cdekOfficesRef = React.useRef(new Map<string, ICDEKOffice>())
  const cdekCellsRef = React.useRef(new Set<string>())
  const ozonRequestIdRef = React.useRef(0)
  const ozonQueryKeyRef = React.useRef('')
  const pickedCodeRef = React.useRef<string | null>(null)

  const [current, setCurrent] = React.useState<ICurrentPoint | null>(null)
  const [isCurrentLoading, setIsCurrentLoading] = React.useState(true)
  const [isCurrentError, setIsCurrentError] = React.useState(false)
  const [isEditing, setIsEditing] = React.useState(false)
  const [cities, setCities] = React.useState<IAddressSuggestion[]>([])
  const [city, setCity] = React.useState<ICityOption | null>(null)
  const [isMapReady, setIsMapReady] = React.useState(false)
  const [loadingCount, setLoadingCount] = React.useState(0)
  const [hasNoPoints, setHasNoPoints] = React.useState(false)
  const [picked, setPicked] = React.useState<IMapPoint | null>(null)
  const [isSaving, setIsSaving] = React.useState(false)

  const isMapShown = isEditing && city !== null
  const currentCode = current?.code ?? null

  React.useEffect(() => {
    let isCancelled = false
    setIsCurrentLoading(true)
    setIsCurrentError(false)
    loadCurrentPoint(provider, sellerId)
      .then((point) => {
        if (!isCancelled) setCurrent(point)
      })
      .catch(() => {
        if (!isCancelled) setIsCurrentError(true)
      })
      .finally(() => {
        if (!isCancelled) setIsCurrentLoading(false)
      })
    return () => {
      isCancelled = true
    }
  }, [provider, sellerId])

  const getPreset = (code: string) =>
    code === pickedCodeRef.current ? PICKED_PRESET : MAP_LOOK[provider].preset

  const repaintPoint = (code: string | null) => {
    if (!code || code === currentCode || !pointsRef.current.has(code)) return
    objectManagerRef.current?.objects.setObjectOptions(code, {
      preset: getPreset(code)
    })
  }

  const addPoints = (list: IMapPoint[]) => {
    const objectManager = objectManagerRef.current
    if (!objectManager) return

    const fresh = list.filter((point) => !pointsRef.current.has(point.code))
    fresh.forEach((point) => pointsRef.current.set(point.code, point))
    objectManager.add(
      fresh.map((point) => ({
        id: point.code,
        type: 'Feature',
        geometry: { type: 'Point', coordinates: point.coords },
        properties: {
          hintContent: point.address,
          ...(provider === 'YANDEX' ? { iconCaption: point.name } : {})
        },
        options: { preset: getPreset(point.code) }
      }))
    )
    setHasNoPoints(pointsRef.current.size === 0)
  }

  const loadPoints = async (request: Promise<IMapPoint[]>) => {
    setLoadingCount((count) => count + 1)
    try {
      addPoints(await request)
    } catch (e) {
      showServerError(e)
    } finally {
      setLoadingCount((count) => count - 1)
    }
  }

  const loadCdekRegion = (coords: TCoords) => {
    cdekCellsRef.current.add(getCdekCell(coords))
    return loadPoints(
      cdekApi.getPvzPointsByCoords(coords[0], coords[1]).then(({ data }) => {
        data.forEach((office) => cdekOfficesRef.current.set(office.code, office))
        return data.map(fromCdekOffice)
      })
    )
  }

  const loadOzonVisible = () => {
    const bounds = mapRef.current?.getBounds() as number[][] | undefined
    if (!city || !bounds || bounds.length < 2) return
    const latitudes = bounds.map((corner) => corner[0])
    const longitudes = bounds.map((corner) => corner[1])
    const queryKey = JSON.stringify([city.name, bounds])
    if (ozonQueryKeyRef.current === queryKey) return
    ozonQueryKeyRef.current = queryKey
    const requestId = ++ozonRequestIdRef.current
    setLoadingCount((count) => count + 1)
    loadOzonDropoffCatalog(city.name, {
      left_bottom: { latitude: Math.min(...latitudes), longitude: Math.min(...longitudes) },
      right_top: { latitude: Math.max(...latitudes), longitude: Math.max(...longitudes) }
    }).then((points) => {
      if (requestId !== ozonRequestIdRef.current || !mapRef.current) return
      addPoints(points)
      setHasNoPoints(points.length === 0)
    }).catch((error) => {
      if (requestId === ozonRequestIdRef.current) ozonQueryKeyRef.current = ''
      showServerError(error)
    }).finally(() => {
      setLoadingCount((count) => count - 1)
    })
  }

  const loadVisible = () => {
    const map = mapRef.current
    if (!map) return

    if (provider === 'CDEK') {
      const center = map.getCenter() as TCoords
      if (cdekCellsRef.current.has(getCdekCell(center))) return
      const isLoadedNearby = Array.from(pointsRef.current.values()).some(
        (point) => distanceKm(center, point.coords) < CDEK_LOADED_RADIUS_KM
      )
      if (!isLoadedNearby) loadCdekRegion(center)
      return
    }

    if (provider === 'OZON') {
      loadOzonVisible()
      return
    }

    const bounds = map.getBounds() as number[][]
    if (!Array.isArray(bounds) || bounds.length < 2) return
    const latitudes = bounds.map((point) => point[0])
    const longitudes = bounds.map((point) => point[1])
    loadPoints(
      yandexDeliveryApi
        .getDropoffStations({
          available_for_dropoff: true,
          latitude: { from: Math.min(...latitudes), to: Math.max(...latitudes) },
          longitude: { from: Math.min(...longitudes), to: Math.max(...longitudes) }
        })
        .then(({ data }) => data.map(fromYandexStation))
    )
  }

  const pickPoint = (point: IMapPoint) => {
    const previousCode = pickedCodeRef.current
    pickedCodeRef.current = point.code
    repaintPoint(previousCode)
    repaintPoint(point.code)
    setPicked(point)
  }

  // Карта подписывается на события один раз, а обработчикам нужны свежие данные
  const handlersRef = React.useRef({ loadVisible, pickPoint, addPoints })
  React.useEffect(() => {
    handlersRef.current = { loadVisible, pickPoint, addPoints }
  })

  React.useEffect(() => {
    if (!isMapShown || !city) return
    let isDestroyed = false
    let debouncedLoadVisible: ReturnType<typeof debounce> | null = null

    const initMap = async () => {
      await loadYandexMaps()
      if (isDestroyed) return
      if (!window.ymaps) {
        return showMessage('error', 'Не удалось загрузить карту', 'Обновите страницу')
      }

      window.ymaps.ready(() => {
        if (isDestroyed || mapRef.current) return

        const map = new window.ymaps.Map(mapContainerId, {
          center: [city.latitude, city.longitude],
          zoom: CITY_ZOOM,
          controls: ['geolocationControl', 'zoomControl', 'fullscreenControl']
        })
        const objectManager = new window.ymaps.ObjectManager({
          clusterize: true,
          gridSize: MAP_LOOK[provider].gridSize
        })
        objectManager.clusters.options.set(
          'preset',
          MAP_LOOK[provider].clusterPreset
        )
        objectManager.objects.events.add('click', (event: any) => {
          const point = pointsRef.current.get(String(event.get('objectId') ?? ''))
          if (point) handlersRef.current.pickPoint(point)
        })
        map.geoObjects.add(objectManager)

        mapRef.current = map
        objectManagerRef.current = objectManager
        // Текущий пункт — отдельной меткой вне кластеров: в кластере его не видно,
        // а перевозчик может не вернуть его в этой области
        const currentPoint = current?.point
        if (currentPoint) {
          pointsRef.current.set(currentPoint.code, currentPoint)
          const placemark = new window.ymaps.Placemark(
            currentPoint.coords,
            {
              hintContent: currentPoint.address,
              ...(provider === 'YANDEX' ? { iconCaption: currentPoint.name } : {})
            },
            { preset: CURRENT_PRESET, zIndex: 1000 }
          )
          placemark.events.add('click', () =>
            handlersRef.current.pickPoint(currentPoint)
          )
          map.geoObjects.add(placemark)
        }
        debouncedLoadVisible = debounce(
          () => handlersRef.current.loadVisible(),
          BOUNDS_DELAY
        )
        map.events.add('boundschange', debouncedLoadVisible)
        setIsMapReady(true)
        handlersRef.current.loadVisible()
      })
    }

    initMap()

    return () => {
      isDestroyed = true
      debouncedLoadVisible?.cancel()
      mapRef.current?.destroy()
      mapRef.current = null
      objectManagerRef.current = null
      pointsRef.current.clear()
      cdekCellsRef.current.clear()
      ozonRequestIdRef.current += 1
      ozonQueryKeyRef.current = ''
      pickedCodeRef.current = null
      setIsMapReady(false)
      setHasNoPoints(false)
    }
  }, [isMapShown])

  React.useEffect(() => {
    if (!city || !isMapReady || !mapRef.current) return

    const coords: TCoords = [city.latitude, city.longitude]
    mapRef.current.setCenter(coords, CITY_ZOOM, { duration: 300 })
    // Город на границе уже загруженного региона иначе остался бы без пунктов
    if (provider === 'CDEK' && !cdekCellsRef.current.has(getCdekCell(coords))) {
      loadCdekRegion(coords)
    }
  }, [city, isMapReady])

  const getCities = React.useCallback(async (search: string) => {
    if (!search.trim()) {
      setCities([])
      return
    }
    try {
      const { data } = await appApi.getAddresses({
        address: search,
        isCityOnly: 'true'
      })
      setCities(data.suggestions)
    } catch (e) {
      showServerError(e)
    }
  }, [])

  const debouncedGetCities = React.useMemo(
    () => debounce(getCities, DEBOUNCE_DELAY),
    [getCities]
  )

  React.useEffect(() => () => debouncedGetCities.cancel(), [debouncedGetCities])

  const closeEditing = () => {
    setIsEditing(false)
    setCity(null)
    setCities([])
    setPicked(null)
  }

  const savePoint = async () => {
    if (!picked) return
    try {
      setIsSaving(true)
      if (provider === 'CDEK') {
        const office = cdekOfficesRef.current.get(picked.code)
        if (!office) {
          showMessage('error', 'Не удалось сохранить пункт', 'Выберите его на карте ещё раз')
          return
        }
        await sellerApi.updateSelectedDeliveryPoint({
          code: office.code,
          name: office.name,
          location: office.location,
          seller_id: sellerId
        })
      } else {
        await sellerApi.updateLogistics(sellerId, provider === 'OZON'
          ? { ozon_dropoff_point_id: Number(picked.code) }
          : { yandex_dropoff_station_id: picked.code })
      }
      setCurrent({ code: picked.code, point: picked })
      setIsCurrentError(false)
      closeEditing()
      message.success('Пункт сдачи сохранён')
    } catch (e) {
      showServerError(e)
    } finally {
      setIsSaving(false)
    }
  }

  const isRequired = provider === 'CDEK' && type === 'profile'
  const isPickedCurrent = picked?.code === currentCode

  const renderCurrent = () => {
    if (isCurrentLoading) {
      return <Typography.Text type='secondary'>Загружаем пункт…</Typography.Text>
    }
    if (isCurrentError) {
      return (
        <Typography.Text type='secondary'>
          Не удалось загрузить выбранный пункт. Обновите страницу
        </Typography.Text>
      )
    }
    if (!current) {
      return <Typography.Text type='secondary'>Пункт не выбран</Typography.Text>
    }
    if (!current.point) {
      return (
        <Typography.Text type='secondary'>
          {provider === 'OZON'
            ? current.label
            : 'Яндекс не нашёл сохранённый пункт. Выберите другой'}
        </Typography.Text>
      )
    }
    return (
      <>
        <Typography.Text strong>{current.point.address}</Typography.Text>
        <Typography.Text type='secondary'>{current.point.name}</Typography.Text>
      </>
    )
  }

  return (
    <section className={styles.section}>
      <Typography.Title level={4} className={styles.title}>
        {TITLE[provider]}
        {isRequired && <span className={styles.required}> *</span>}
      </Typography.Title>
      <Typography.Text>
        Выберите пункт, в который {type === 'profile' ? 'вы будете' : 'продавец будет'}{' '}
        передавать заказы {CARRIER[provider]}.
      </Typography.Text>

      <div className={styles.currentRow}>
        <div className={styles.current}>
          <EnvironmentOutlined className={styles.currentIcon} />
          <div className={styles.pointInfo}>{renderCurrent()}</div>
        </div>
        <Button
          type={isEditing ? 'default' : 'primary'}
          htmlType='button'
          size='large'
          disabled={isCurrentLoading}
          onClick={isEditing ? closeEditing : () => setIsEditing(true)}
        >
          {isEditing ? 'Отмена' : current ? 'Изменить' : 'Выбрать'}
        </Button>
      </div>

      {isEditing && (
        <Select
          showSearch
          value={city?.name}
          placeholder='Введите город'
          notFoundContent={null}
          filterOption={false}
          size='large'
          className={styles.citySelect}
          onSearch={debouncedGetCities}
          onSelect={(value: string) => {
            setCity(JSON.parse(value) as ICityOption)
          }}
          options={cities.map((item) => ({
            value: JSON.stringify({
              name: item.value,
              latitude: Number(item.data.geo_lat),
              longitude: Number(item.data.geo_lon)
            } satisfies ICityOption),
            label: item.value
          }))}
        />
      )}

      {isMapShown && (
        <>
          <Typography.Text type='secondary' className={styles.mapHint}>
            Нажмите на пункт на карте.
            {current?.point && ' Красная метка — выбранный сейчас пункт.'}
          </Typography.Text>
          <div className={styles.mapWrapper}>
            <div id={mapContainerId} className={styles.map}></div>
            {(!isMapReady || loadingCount > 0) && (
              <div className={styles.loadingOverlay}>
                <Spin />
              </div>
            )}
          </div>

          {hasNoPoints && loadingCount === 0 && (
            <Typography.Text type='secondary' className={styles.emptyText}>
              Пунктов здесь нет. Переместите карту или выберите другой город.
            </Typography.Text>
          )}

          {picked && (
            <div className={styles.picked}>
              <div className={styles.pointInfo}>
                <Typography.Text strong>{picked.address}</Typography.Text>
                <Typography.Text type='secondary'>{picked.name}</Typography.Text>
                {picked.workTime && <Typography.Text>{picked.workTime}</Typography.Text>}
              </div>
              <Button
                type='primary'
                htmlType='button'
                size='large'
                loading={isSaving}
                disabled={isPickedCurrent}
                onClick={savePoint}
              >
                {isPickedCurrent ? 'Уже выбран' : 'Выбрать этот пункт'}
              </Button>
            </div>
          )}
        </>
      )}
    </section>
  )
}
